import { showSuccess, showError } from '@calendly/alert'
import { I18n } from '@calendly/i18n'
import { createEntityAdapter } from '@reduxjs/toolkit'

import { setLocalStorageAlert } from 'hooks/use_local_storage_alerts'
import { rootApi } from 'root-api'

const accountsAdapter = createEntityAdapter({
  selectId: (instance) => instance.uuid,
  selectById: (data, uuid) => data.find((instance) => instance.uuid === uuid)
})

const addOauthQueryParams = (url, params) => {
  if (!url) return url

  const [baseUrl, queryString = ''] = url.split('?')
  const searchParams = new URLSearchParams(queryString)

  Object.entries(params).forEach(([key, value]) => {
    if (value != null && value !== '') {
      searchParams.set(key, value)
    }
  })

  const search = searchParams.toString()

  return search ? `${baseUrl}?${search}` : baseUrl
}

const api = rootApi.enhanceEndpoints({ addTagTypes: ['CALENDAR_ACCOUNT'] }).injectEndpoints({
  endpoints: (build) => ({
    fetchMetadata: build.query({
      query: () => ({ url: '/api/calendar_accounts/metadata' })
    }),
    fetchCalendarAccounts: build.query({
      query: () => ({ url: '/api/calendar_accounts' }),
      transformResponse: (response) => accountsAdapter.addMany(
        accountsAdapter.getInitialState(),
        response
      ),
      providesTags: [{ type: 'CALENDAR_ACCOUNT', id: 'LIST' }]
    }),
    updateCalendarAccount: build.mutation({
      query: ({ id, pullCalendarIds, pushCalendarId, unavailableStatuses, webhooksEnabled }) => ({
        url: `/api/calendar_accounts/${id}`,
        method: 'PATCH',
        data: {
          pullCalendarIds,
          pushCalendarId,
          unavailableStatuses,
          webhooksEnabled
        }
      }),
      extraOptions: {
        onSuccess: (response, { dispatch }) => {
          dispatch(showSuccess(I18n.t('publisher.flash.settings_saved')))
          dispatch(api.util.updateQueryData('fetchCalendarAccounts', undefined, (draft) => {
            accountsAdapter.setAll(draft, response)
          }))
        }
      }
    }),
    disablePush: build.mutation({
      query: ({ calendarAccountUUID }) => ({
        url: '/api/calendar_accounts/disable_push',
        method: 'POST',
        data: {
          calendarAccountUUID
        }
      }),
      extraOptions: {
        onSuccess: (response, { dispatch }) => {
          dispatch(showSuccess(I18n.t('publisher.flash.settings_saved')))
          dispatch(api.util.updateQueryData('fetchCalendarAccounts', undefined, (draft) => {
            accountsAdapter.setAll(draft, response)
          }))
        }
      }
    }),
    disconnectCalendar: build.mutation({
      query: (id) => ({
        url: `/api/calendar_accounts/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: (_, error) => (error ? [] : [{ type: 'CALENDAR_ACCOUNT', id: 'LIST' }]),
      extraOptions: {
        handleErrors: false
      },
      onQueryStarted: async (id, { queryFulfilled, dispatch, getState }) => {
        const { data } = api.endpoints.fetchCalendarAccounts.select()(getState())
        if (!data) return

        const { name } = accountsAdapter.getSelectors().selectById(data, id)

        try {
          await queryFulfilled
        } catch (e) {
          if (!['request_aborted', 'missing'].includes(e.error?.type)) {
            dispatch(showError(e.error.message))

            return
          }
        }

        setLocalStorageAlert(
          I18n.t('publisher.flash.successfully_disconnected', {
            name: I18n.t('publisher.calendar.name', { name })
          }),
          'success'
        )

        dispatch(showSuccess(
          I18n.t('publisher.flash.successfully_disconnected', {
            name: I18n.t('publisher.calendar.name', { name })
          })
        ))
      }
    }),
    fetchIcloudData: build.query({
      query: (id) => ({
        url: `/api/calendars/icloud/${id}`
      })
    }),
    connectIcloud: build.mutation({
      query: ({ email, password, calendarAccountId }) => ({
        url: '/api/calendars/icloud',
        method: 'POST',
        data: {
          calendarAccountId,
          calendarAccount: {
            email,
            password
          }
        }
      }),
      invalidatesTags: (_, error) => (error ? [] : [{ type: 'CALENDAR_ACCOUNT', id: 'LIST' }]),
      extraOptions: {
        onSuccess: (_, { dispatch }) => {
          dispatch(showSuccess(I18n.t('publisher.calendar.connect_success', { calendar: 'iCloud Calendar' })))
        }
      }
    }),
    fetchExchangeData: build.query({
      query: (id) => ({
        url: `/api/calendars/exchange/${id}`
      })
    }),
    connectExchange: build.mutation({
      query: ({ email, server, user, password, domain, calendarAccountId, skipBrowserHistoryBack = false }) => ({
        url: '/api/calendars/exchange',
        method: 'POST',
        skipBrowserHistoryBack,
        data: {
          calendarAccountId,
          calendarAccount: {
            email,
            password,
            server,
            externalId: user ? [user, domain].filter(Boolean).join('@') : '',
          }
        }
      }),
      invalidatesTags: (_, error) => (error ? [] : [{ type: 'CALENDAR_ACCOUNT', id: 'LIST' }]),
      extraOptions: {
        onSuccess: (_, { dispatch }, { skipBrowserHistoryBack } = {}) => {
          if (!skipBrowserHistoryBack) window.history.back()
          setLocalStorageAlert(I18n.t('publisher.calendar.connect_success', { calendar: 'Exchange Calendar' }), 'success')
          dispatch(showSuccess(I18n.t('publisher.calendar.connect_success', { calendar: 'Exchange Calendar' })))
        }
      }
    }),
    fetchOutlookDesktopData: build.query({
      query: (id) => ({
        url: `/api/calendars/outlook_desktop/${id}`
      })
    }),
    connectOutlookDesktop: build.mutation({
      query: () => ({
        url: '/api/calendars/outlook_desktop',
        method: 'POST',
      })
    }),
    connectOauth: build.mutation({
      query: ({ provider, calendarAccountId, customUrl = null, flow = null, redirect = null, returnTo = null }) => {
        const url = {
          google: '/users/auth/google_oauth2?state=calendar&flow=settings',
          outlook: '/users/auth/azure2_oauth2?state=calendar&flow=settings',
          custom: customUrl
        }[provider]

        return ({
          url: addOauthQueryParams(url, {
            calendar_account_id: calendarAccountId,
            return_to: returnTo,
          }),
          method: 'POST',
          redirect,
          ...(flow && { flow }),
        })
      },
      extraOptions: {
        onSuccess: (response) => {
          window.location.assign(response.url)
        }
      }
    })
  })
})

export const {
  useFetchMetadataQuery,
  useFetchCalendarAccountsQuery,
  useUpdateCalendarAccountMutation,
  useDisablePushMutation,
  useDisconnectCalendarMutation,
  useFetchIcloudDataQuery, useConnectIcloudMutation,
  useFetchExchangeDataQuery, useConnectExchangeMutation,
  useFetchOutlookDesktopDataQuery, useConnectOutlookDesktopMutation,
  useConnectOauthMutation
} = api
export const { selectTotal, selectIds, selectById, selectAll } = accountsAdapter.getSelectors()
